import { handleSessionError } from './ErrorHandler'

// Helper function to determine if the environment is development
const isDevEnvironment = import.meta.env.VITE_APP_VERSION === 'DEV'

export interface ClientPrincipal {
  identityProvider?: string
  userId?: string
  userDetails?: string
  userRoles?: string[]
  claims?: Array<{ typ: string; val: string }>
}

const FALLBACK_PRINCIPAL: ClientPrincipal = { userRoles: [] }

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const hasIdentity = (principal: ClientPrincipal) =>
  Boolean(principal.userId || principal.userDetails || principal.identityProvider)

const isRolesMissing = (principal: ClientPrincipal) =>
  hasIdentity(principal) && !(principal.userRoles ?? []).includes('authenticated')

const MOCK_CLIENT_PRINCIPAL: ClientPrincipal = {
  identityProvider: 'github',
  userId: 'mock-user-id',
  userDetails: 'mock-user',
  userRoles: ['authenticated', 'anonymous', 'prospector', 'norhybrid_admin'],
  claims: [
    { typ: 'name', val: 'Mock User' },
    { typ: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name', val: 'Mock User' }
  ]
}

export const getUserInfo = (): ClientPrincipal => {
  const request = new XMLHttpRequest()
  request.open('GET', '/.auth/me', false) // `false` makes the request synchronous
  try {
    request.send(null)

    if (request.status !== 200) {
      throw new Error(`Request failed with status ${request.status}`)
    }

    const response = JSON.parse(request.responseText)

    if (!response?.clientPrincipal) {
      console.error('No clientPrincipal found:', response)

      if (isDevEnvironment) {
        console.warn('DEV environment detected. Returning mock principal.')
        return MOCK_CLIENT_PRINCIPAL
      }

      handleSessionError('Your session has expired. Please refresh the page.')
      return { userRoles: [] } // Default fallback value
    } else if (!response?.clientPrincipal.claims) {
      if (isDevEnvironment) {
        console.warn('DEV environment detected. Skipping claims check.')
      } else {
        console.warn('No claims found:', response)
        handleSessionError('Your session has expired. Please refresh the page.')
        return { userRoles: [] } // Default fallback value
      }
    }

    return response.clientPrincipal
  } catch (error) {
    if (isDevEnvironment) {
      console.warn('DEV environment detected and /.auth/me failed. Returning mock principal.')
      return MOCK_CLIENT_PRINCIPAL
    }
    console.error('Error parsing /auth/me response:', error)
    return { userRoles: [] } // Default fallback value
  }
}

/**
 * Async `/.auth/me` fetch with a short retry policy for transient "roles missing" states.
 *
 * NOTE: This does not call `handleSessionError()`; callers (e.g. `MainStore.refresh()`) must
 * decide whether to trigger recovery UX.
 */
export const fetchUserInfo = async (): Promise<ClientPrincipal> => {
  const retryDelaysMs = [0, 200, 500]

  for (let attempt = 0; attempt < retryDelaysMs.length; attempt++) {
    if (retryDelaysMs[attempt] > 0) {
      await sleep(retryDelaysMs[attempt])
    }

    try {
      const response = await fetch('/.auth/me', { cache: 'no-store' })

      // Non-OK is treated as inability to validate session (caller handles recovery).
      if (!response.ok) {
        if (isDevEnvironment) {
          console.warn('DEV environment detected and /.auth/me failed. Returning mock principal.')
          return MOCK_CLIENT_PRINCIPAL
        }
        console.warn('Non-OK /.auth/me response:', response.status)
        return FALLBACK_PRINCIPAL
      }

      const json = (await response.json()) as { clientPrincipal?: ClientPrincipal } | null
      const principal = json?.clientPrincipal

      if (!principal) {
        if (isDevEnvironment) {
          console.warn('DEV environment detected and /.auth/me returned no principal. Returning mock principal.')
          return MOCK_CLIENT_PRINCIPAL
        }
        console.warn('No clientPrincipal found in /.auth/me response:', json)
        return FALLBACK_PRINCIPAL
      }

      // Mirror current DEV behavior: claims can be missing in DEV without hard-failing.
      if (!principal.claims && isDevEnvironment) {
        console.warn('DEV environment detected. Skipping claims check.')
      }

      // Retry only for the "roles missing" symptom.
      if (isRolesMissing(principal) && attempt < retryDelaysMs.length - 1) {
        continue
      }

      return principal
    } catch (error) {
      if (isDevEnvironment) {
        console.warn('DEV environment detected and fetch /.auth/me failed. Returning mock principal.')
        return MOCK_CLIENT_PRINCIPAL
      }
      console.error('Error fetching /.auth/me:', error)
      return FALLBACK_PRINCIPAL
    }
  }

  return FALLBACK_PRINCIPAL
}
